import React from 'react';
import { fmtNumber } from './kit';

// Shared attention "finding" renderer for the ClientHub — used by each list row
// (ClientRow) and the client page so a client reads the same
// in both places: coloured metric tokens against plain gray context, with
// the sentence starting on a capital. What each metric and flag MEANS is explained
// once in the list's "How to read these" legend, not repeated on every row.

// Metric colours — a leak reads red, a wait amber, open work brand-blue.
export const TONE = {
  red: 'text-red-600 dark:text-red-400',
  amber: 'text-amber-600 dark:text-amber-400',
  blue: 'text-[#32B1FF]',
};

// A metric segment: a count + label in its tone, or null when the count is zero.
const seg = (count, label, tone) => (count ? { num: fmtNumber(count), label, tone } : null);
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// How badly a client is flagged, from the leak metrics — a compact badge instead
// of a canned sentence, so the numbers carry the "why" while the badge escalates
// the "how bad": 37 broken promises reads Critical, 2 reads At risk. Broken
// promises (we failed them) are the strongest signal; overdue is a lighter flag.
// Waiting/help alone don't earn a severity badge — they're pending, not damage.
// `rule` is the sentence that states where the level starts, in the same terms flagLevel()
// applies. A row's chip shows it on hover and the list legend prints it, so the rule is
// written once.
export const FLAG = {
  critical: { label: 'Critical', cls: 'bg-red-600 text-white dark:bg-red-600 dark:text-white', rule: '10 or more broken promises.' },
  high: { label: 'High risk', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300', rule: '3 to 9 broken promises.' },
  watch: { label: 'At risk', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', rule: '1 to 2 broken promises, or any overdue work.' },
};
export const flagLevel = (broken, overdue) => {
  if (broken >= 10) return 'critical';
  if (broken >= 3) return 'high';
  if (broken >= 1 || overdue >= 1) return 'watch';
  return null;
};

/**
 * The metric segments of a client's situation, split into what is leaking
 * (broken promises, overdue) and what is merely standing (open, waiting, help).
 * @param {{broken?:number,overdue?:number,waiting?:number,open?:number,help?:number}} counts
 * @returns {{leaking:object[], standing:object[]}}
 */
const buildSegments = ({ broken = 0, overdue = 0, waiting = 0, open = 0, help = 0 } = {}) => ({
  leaking: [
    seg(broken, broken === 1 ? 'broken promise' : 'broken promises', 'red'),
    seg(overdue, 'overdue', 'red'),
  ].filter(Boolean),
  standing: [
    seg(open, 'open', 'blue'),
    seg(waiting, 'waiting on them', 'amber'),
    seg(help, help === 1 ? 'needs help' : 'need help', 'amber'),
  ].filter(Boolean),
});

/**
 * The ONE main signal for a client — broken promises, then overdue, then open /
 * waiting / help — as a { num, label, tone } segment, or null when nothing is
 * counted. The same priority the finding's headline uses.
 * @param {object} counts  Same shape as AttentionFinding's `counts`.
 * @returns {{num:string,label:string,tone:"red"|"amber"|"blue"}|null}
 */
export const leadSegment = (counts) => {
  const { leaking, standing } = buildSegments(counts || {});
  return [...leaking, ...standing][0] || null;
};

// Metric segments joined by a middot. The whole metric (count + label) takes its
// tone colour, so it reads as a distinct token against the plain gray context. Every
// label is capitalised on its first letter so a token reads the same
// wherever it sits — "16 Open" whether it leads or trails, never "16 open" on one
// row and "9 Open" on another.
function Segments({ items }) {
  return items.map((s, i) => (
    <React.Fragment key={i}>
      {i > 0 && ' · '}
      <span className={TONE[s.tone]}>
        {s.num} {cap(s.label)}
      </span>
    </React.Fragment>
  ));
}

/**
 * The composed situation for a client: leaking metrics as the headline, the
 * standing metrics + quiet recency as the sub. Counts are pre-normalized so both
 * callers can pass their own field names.
 *
 * @param {object} props
 * @param {{broken?:number,overdue?:number,waiting?:number,open?:number,help?:number,quiet?:number}} props.counts
 * @param {string} [props.headlineClassName]  Font/size/colour for the headline line.
 */
export default function AttentionFinding({
  counts,
  headlineClassName = 'text-sm text-gray-800 dark:text-gray-200',
}) {
  const { broken = 0, overdue = 0, waiting = 0, open = 0, help = 0, quiet = null } = counts || {};

  const { leaking, standing } = buildSegments({ broken, overdue, waiting, open, help });

  const context = 'not-italic font-normal text-gray-500 dark:text-gray-400';
  const level = flagLevel(broken, overdue);

  // Lead with ONE main signal — the headline is a single metric, never two stacked
  // together. Priority: broken promises, then overdue, then open / waiting / help.
  // Every other metric drops to the sub, so the card reads "here is the one thing,
  // and here is the rest".
  const segsAll = [...leaking, ...standing];
  const lead = segsAll[0] || null;
  const rest = segsAll.slice(1);

  // Last-contact recency ALWAYS closes a card that has a finding, so every such card
  // ends the same way — a value, "today", or an explicit "unknown" — instead of some
  // cards dropping the line, which reads as a hidden rule rather than missing data.
  // `quiet` is days since last contact: null when there is no record, 0 when it was
  // today. The pure "nothing needs attention" state (no lead) stays clean, no line.
  //
  // On a LEAKING (red-flagged) client, the recency line NAMES what still holds the
  // flag — broken promises are a running historical tally and overdue work stays open
  // regardless of a call — so a flagged card is never just "Last spoke today / N days
  // ago" with no stated reason it is still urgent (markup #17). The suffix rides every
  // recency value, not only "today", so the explanation is consistent across cards.
  // Non-leaking cards keep the plain line: there is nothing "still on record" to state.
  const leakSuffix = broken > 0
    ? ' — promises still on record'
    : overdue > 0
      ? ' — overdue work still open'
      : '';
  const lastContactLine = !lead
    ? null
    : quiet == null
      ? `Last contact unknown${leakSuffix}.`
      : quiet === 0
        ? `Last spoke today${leakSuffix}.`
        : `Last spoke ${fmtNumber(quiet)} ${quiet === 1 ? 'day' : 'days'} ago${leakSuffix}.`;

  return (
    <>
      <p className={headlineClassName}>
        {lead ? (
          <>
            <Segments items={[lead]} />
            {level && (
              <>
                {' '}
                <span className={`not-italic ml-1 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold align-[1px] ${FLAG[level].cls}`}>
                  {FLAG[level].label}
                </span>
              </>
            )}
          </>
        ) : (
          <span className={context}>Nothing needs attention right now.</span>
        )}
      </p>
      {rest.length > 0 && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          <Segments items={rest} />
        </p>
      )}
      {/* The last-contact line sits on its OWN line, below the standing metrics — the
          reason a flagged client is still urgent ("… — promises still on record") can
          run long, and folding it onto the metrics line crowded that row. */}
      {lastContactLine && (
        <p className={`${rest.length > 0 ? 'mt-0.5' : 'mt-1'} text-sm text-gray-500 dark:text-gray-400`}>
          {lastContactLine}
        </p>
      )}
    </>
  );
}
